import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { http, saveSessionToken } from "@/api";
import { useAuth } from "@/auth/AuthProvider";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";

// Emergent Google Auth returns to `{origin}/#session_id=<one-shot>`. Detect that hash on ANY
// route render (see App.jsx routing), exchange the session_id for a persistent server session,
// then navigate to `/` cleanly. Uses `useRef` (not state) for the processed flag so React
// StrictMode's double-invocation doesn't call the endpoint twice.
//
// SAFARI iOS HARDENING (2026-08-08): Previously we silently redirected to Login on any error,
// which produced an infinite-tap loop when the exchange failed — user never saw why. Now:
//   1. Retry the POST once on network/5xx failure (Safari sometimes drops the first request
//      after a cross-site redirect while it re-establishes storage access).
//   2. On terminal failure, show a visible error card with the reason + a Try Again button so
//      the loop stops and the user can share the specific error.
//   3. Log the raw error to console for later inspection.
export const AuthCallback = () => {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const processed = useRef(false);
    const [error, setError] = useState(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        const params = new URLSearchParams(location.hash.replace(/^#/, ""));
        const sessionId = params.get("session_id");
        if (!sessionId) {
            navigate("/", { replace: true });
            return;
        }

        // Try up to 2 times (initial + 1 retry) with a 600ms backoff. `session_id` is
        // single-use, so if the backend actually consumed it on attempt #1 we'd get a 401 on
        // attempt #2 — which the catch handles by surfacing the specific error.
        const tryExchange = async (retriesLeft) => {
            try {
                const r = await http.post("/auth/session", { session_id: sessionId });
                // Safari-fallback: persist the token client-side so we can also send it as
                // Authorization: Bearer on future requests when the cookie gets ITP-purged.
                if (r.data?.session_token) saveSessionToken(r.data.session_token);
                if (!r.data?.user) throw new Error("Auth response missing user data");
                setUser(r.data.user);
                // Clear the hash before navigating so back-button doesn't retrigger the exchange.
                window.history.replaceState({}, "", "/");
                navigate("/", { replace: true });
            } catch (e) {
                console.error("auth callback failed", { attempt: 3 - retriesLeft, error: e });
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail || e?.message || "Unknown error";
                // Retry once on transient failures (no response, or 5xx). Don't retry on
                // 4xx — those are deterministic (bad/expired session_id, missing email, etc.).
                const isTransient = !e?.response || (status >= 500 && status < 600);
                if (isTransient && retriesLeft > 0) {
                    setAttempt((a) => a + 1);
                    setTimeout(() => tryExchange(retriesLeft - 1), 600);
                    return;
                }
                setError({ status, detail });
            }
        };

        tryExchange(1);
    }, [location.hash, navigate, setUser]);

    if (error) {
        return (
            <div className="min-h-screen bg-meye-bg text-zinc-100 flex items-center justify-center p-6">
                <div className="max-w-md w-full rounded-2xl border border-red-500/40 bg-meye-card/80 backdrop-blur-md p-6 space-y-4">
                    <div className="flex items-center gap-2 text-red-400">
                        <AlertCircle size={18} strokeWidth={2} />
                        <h2 className="font-brand text-sm uppercase tracking-widest">Sign-in failed</h2>
                    </div>
                    <p className="text-xs text-zinc-400 font-mono leading-relaxed">
                        We couldn&apos;t finish exchanging your Google session. This can happen on
                        Safari after a slow network hop — the session_id is single-use and
                        may have expired between the redirect and here.
                    </p>
                    <div className="rounded-md border border-meye-border bg-black/40 p-3 text-[11px] font-mono text-zinc-500 space-y-1" data-testid="auth-error-detail">
                        {error.status && <div>status: <span className="text-red-300">{error.status}</span></div>}
                        <div className="break-words">detail: <span className="text-red-300">{String(error.detail).slice(0, 200)}</span></div>
                        <div>attempts: <span className="text-zinc-300">{attempt + 1}</span></div>
                    </div>
                    <button
                        onClick={() => {
                            // Full reload back to root (no hash) so a fresh Google flow can start.
                            window.location.replace("/");
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-brand font-bold text-sm hover:opacity-90 transition-opacity"
                        data-testid="auth-retry-btn"
                    >
                        <RefreshCw size={14} strokeWidth={2.5} />
                        Try again
                    </button>
                    <p className="text-[10px] text-zinc-600 text-center font-mono">
                        If this keeps happening, quit Safari fully and try again — iOS ITP
                        sometimes needs the tab session to reset.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-meye-bg text-zinc-100 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-meye-cyan font-mono text-sm uppercase tracking-widest">
                <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    establishing link…
                </div>
                {attempt > 0 && (
                    <div className="text-[10px] text-zinc-500" data-testid="auth-retry-indicator">
                        retry {attempt}/1 — safari cookie handshake
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthCallback;
